import { createApp } from 'vue'
import portalVue from 'portal-vue'
import App from '@/App.vue'
import { logSentry } from '@gto/sentry'
import router from '@/router'
import { createVuetify } from 'vuetify'
import store from '@/store'
import { i18n } from '@/locales/i18n'
import initSentry from '@/sentry'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { createManager as createYouTubeManager } from '@vue-youtube/core'
import { connectRouterBridge } from '@/router/connect-router-bridge'

// keep here so postcss-sass-unicode does not screw up
import '@mdi/font/css/materialdesignicons.min.css'
import '@/styles/styles.scss'

import '@/routerHooks'

const app = createApp(App)

initSentry(app, router)

// Vuetify
const gwTheme = {
  dark: true,
  colors: {
    primary: '00AC8D',
  },
}
const vuetify = createVuetify({
  theme: {
    defaultTheme: 'gwTheme',
    themes: {
      gwTheme,
    },
  },
})
app.use(vuetify)

app.use(VueQueryPlugin)
app.use(store)
app.use(router)
app.use(i18n)
app.use(portalVue)
app.use(createYouTubeManager())

router.isReady().then(() => {
  app.mount('#app')

  connectRouterBridge()
})

/**
 * Handle Vite preload errors with immediate first attempt and timeout for retries
 * @see https://vite.dev/guide/build#load-error-handling
 */
function handleVitePreloadErrors() {
  const MAX_PRELOAD_ERROR_RETRIES = 5
  const RELOAD_TIMEOUT_MS = 1000
  const PRELOAD_ERROR_STORAGE_KEY = 'vite_preload_error_count'

  // Reset preload error counter after a delay.
  setTimeout(() => {
    localStorage.removeItem(PRELOAD_ERROR_STORAGE_KEY)
  }, 5_000)

  window.addEventListener('vite:preloadError', event => {
    // Console dir for expanded object
    console.dir('vite:preloadError: ', event)

    const preloadErrorRetryCount = Number(localStorage.getItem(PRELOAD_ERROR_STORAGE_KEY) || '0')

    if (preloadErrorRetryCount < MAX_PRELOAD_ERROR_RETRIES) {
      // Store updated count in localStorage
      localStorage.setItem(PRELOAD_ERROR_STORAGE_KEY, String(preloadErrorRetryCount + 1))

      if (preloadErrorRetryCount === 0) {
        // First error - handle immediately
        console.info(`Executing reload attempt 1/${MAX_PRELOAD_ERROR_RETRIES}`)
        window.location.reload()
      } else {
        // Subsequent errors - use timeout
        console.info(
          `Scheduling reload attempt ${preloadErrorRetryCount + 1}/${MAX_PRELOAD_ERROR_RETRIES} in ${RELOAD_TIMEOUT_MS}ms`,
        )

        setTimeout(() => {
          console.info(
            `Executing reload attempt ${preloadErrorRetryCount + 1}/${MAX_PRELOAD_ERROR_RETRIES}`,
          )
          window.location.reload()
        }, RELOAD_TIMEOUT_MS)
      }
    } else {
      logSentry(
        'Maximum preload error retry attempts reached. Please check your network connection or try again later.',
        event.payload,
      )

      // Reset preload error counter after a delay.
      setTimeout(() => {
        localStorage.removeItem(PRELOAD_ERROR_STORAGE_KEY)
        console.info('Preload error counter reset after successful app load')
      }, 10_000)
    }
  })
}

handleVitePreloadErrors()

// PRODUCTION HACKS vue3
// document.getElementById('app').__vue_app__.config.globalProperties.$store.stategt

// link dump
// https://polypane.app/css-3d-transform-examples/
